import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { useVirtualizer } from '@tanstack/react-virtual'
import BeatLoader from 'react-spinners/BeatLoader'

import style from 'pages/vault/_vault.module.scss'
import {
  type ClientSortKey,
  VaultCardGrouped,
} from 'pages/vault/components/VaultCardGrouped'
import { expandVaultEntries } from 'pages/vault/helpers/expandVaultEntries'

type Props = {
  drawerOpen: boolean
  isLoading: boolean
  filteredMedia: any[]
  toggleSelectedPosts: (v: any) => void
  allSelectedMedia: any[]
  selectedPosts: any[]
  postsGrouped: any
  postsSort: ClientSortKey
  showDuplicates: boolean
  allTimeStats: boolean
  postsFilter: 'all_media' | 'photos' | 'videos'
  onSelectVariant: (variant: any) => void
  isVariantSelected: (key: string) => boolean
  getVariantIndex: (key: string) => number | null

  // pagination
  shouldPaginate: boolean
  hasNextPage: any
  isFetchingNextPage: boolean
  fetchNextPage?: (options?: {
  cancelRefetch?: boolean
}) => Promise<any>
  isFetching: boolean
  isHydrated: boolean
}

function isWindowScrollEl(el: HTMLElement | null) {
  if (!el) return true
  return el === document.documentElement || el === document.body
}

export function VaultCardsGrid(props: Props) {
  const {
    drawerOpen,
    isLoading,
    filteredMedia,
    toggleSelectedPosts,
    allSelectedMedia,
    selectedPosts,
    postsGrouped,
    allTimeStats = false,
    showDuplicates,
    postsFilter,
    postsSort,

    onSelectVariant,
    isVariantSelected,
    getVariantIndex,
    shouldPaginate,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,

    isFetching,
    isHydrated,
  } = props

  const gridRef = useRef<HTMLDivElement | null>(null)

  // Find nearest scrollable parent (for react-virtual root).
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null)

  const isBusy = !isHydrated || isLoading || isFetching

  useLayoutEffect(() => {
    const start = gridRef.current
    if (!start) return

    let el: HTMLElement | null = start
    while (el) {
      const cs = getComputedStyle(el)
      const oy = cs.overflowY

      // include overlay (common on macOS/iOS)
      if (oy === 'auto' || oy === 'scroll' || oy === 'overlay') {
        setScrollEl(el)
        return
      }
      el = el.parentElement
    }

    // Fallback for window scrolling:
    // react-virtual works fine with documentElement here.
    setScrollEl(document.documentElement)
  }, [])

  // Columns are driven by CSS var: --vault-cols
  const [cols, setCols] = useState(3)

  useEffect(() => {
    const el = gridRef.current
    if (!el) return

    const readColsFromCssVar = () => {
      const cs = getComputedStyle(el)
      const raw = cs.getPropertyValue('--vault-cols').trim()
      const n = parseInt(raw, 10)
      setCols(Number.isFinite(n) && n > 0 ? n : 1)
    }

    readColsFromCssVar()

    const ro = new ResizeObserver(() => readColsFromCssVar())
    ro.observe(el)

    window.addEventListener('orientationchange', readColsFromCssVar)
    window.addEventListener('resize', readColsFromCssVar)

    return () => {
      ro.disconnect()
      window.removeEventListener('orientationchange', readColsFromCssVar)
      window.removeEventListener('resize', readColsFromCssVar)
    }
  }, [])

  const items = useMemo(() => {
    const list = Array.isArray(filteredMedia) ? filteredMedia : []
    // when grouped=false, expand so each card is 1 thumb (virtualizer-friendly)
    return postsGrouped ? list : expandVaultEntries(list, postsFilter)
  }, [filteredMedia, postsGrouped, postsFilter])

  // Group items into rows based on cols.
  const rows = useMemo(() => {
    const out: any[][] = []
    const list = Array.isArray(items) ? items : []
    const c = Math.max(1, cols || 1)

    for (let i = 0; i < list.length; i += c) {
      out.push(list.slice(i, i + c))
    }

    return out
  }, [items, cols])


const showInitialLoader =
  rows.length === 0 &&
  (isLoading || (!isHydrated && isFetching))

const showEmptyState =
  rows.length === 0 &&
  !isLoading &&
  !isFetching

  // Keep it simple + stable.
  // overscan applies BOTH directions (up + down).
  // This prevents “top row disappears” bugs caused by overly aggressive unmounting.
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollEl,
    estimateSize: () => 116,
    overscan: 2,
  })

  const totalSize = rowVirtualizer.getTotalSize()
  const virtualRows = rowVirtualizer.getVirtualItems()

  // Pagination trigger: when we're within N rows of the end.

const fetchInFlightRef = useRef(false)

useEffect(() => {
  if (!isFetchingNextPage) {
    fetchInFlightRef.current = false
  }
}, [isFetchingNextPage])

useEffect(() => {
  if (!shouldPaginate) return

  const checkPagination = () => {
    if (!hasNextPage) return
    if (isLoading || isFetchingNextPage) return
    if (fetchInFlightRef.current) return
    if (!fetchNextPage) return
    if (!rows.length) return

    const virtualItems = rowVirtualizer.getVirtualItems()
    if (!virtualItems.length) return

    const grid = gridRef.current
    if (!grid) return

    const gridRect = grid.getBoundingClientRect()

    /*
     * Convert the viewport into coordinates relative to the
     * virtualized grid.
     */
    const viewportTop = Math.max(0, -gridRect.top)

    const viewportBottom = Math.min(
      totalSize,
      window.innerHeight - gridRect.top
    )

    let firstVisibleRow = -1
    let lastVisibleRow = -1

    for (let i = 0; i < virtualItems.length; i++) {
      const item = virtualItems[i]

      const itemTop = item.start
      const itemBottom = item.start + item.size

      const intersects =
        itemBottom > viewportTop &&
        itemTop < viewportBottom

      if (!intersects) continue

      if (firstVisibleRow === -1) {
        firstVisibleRow = item.index
      }

      lastVisibleRow = item.index
    }

    if (
      firstVisibleRow === -1 ||
      lastVisibleRow === -1
    ) {
      return
    }

    /*
     * How many rows are ACTUALLY visible right now?
     *
     * Mobile may be ~5.
     * Desktop may be ~8-10.
     */
    const visibleRowCount =
      lastVisibleRow - firstVisibleRow + 1

    /*
     * Fetch when roughly 3 rows remain after the viewport.
     */
    const prefetchRows = Math.max(3, visibleRowCount)

    const rowsRemaining =
      rows.length - 1 - lastVisibleRow
console.log('[VAULT_PAGINATION]', {
  cols,

  // viewport
  windowHeight: window.innerHeight,
  gridTop: gridRect.top,
  gridBottom: gridRect.bottom,
  viewportTop,
  viewportBottom,

  // rows
  totalRows: rows.length,
  firstVisibleRow,
  lastVisibleRow,
  visibleRowCount,
  visibleCards: visibleRowCount * cols,

  // trigger
  prefetchRows,
  rowsRemaining,
  shouldTrigger: rowsRemaining <= prefetchRows,

  // query state
  shouldPaginate,
  hasNextPage,
  isLoading,
  isFetchingNextPage,
  fetchInFlight: fetchInFlightRef.current,

  // virtualizer
  virtualIndexes: virtualItems.map(item => item.index),
  virtualCount: virtualItems.length,
  totalSize,
})
    if (rowsRemaining > prefetchRows) {
      return
    }

    fetchInFlightRef.current = true

    void fetchNextPage({
      cancelRefetch: false,
    })
  }

  /*
   * Check once after the current layout has settled.
   *
   * This matters on a very tall desktop viewport:
   * if page 1 genuinely doesn't fill the screen enough,
   * page 2 SHOULD be requested.
   */
  const frame = requestAnimationFrame(checkPagination)

  const target =
    scrollEl && !isWindowScrollEl(scrollEl)
      ? scrollEl
      : window

  target.addEventListener(
    'scroll',
    checkPagination,
    { passive: true }
  )

  window.addEventListener(
    'resize',
    checkPagination,
    { passive: true }
  )

  return () => {
    cancelAnimationFrame(frame)

    target.removeEventListener(
      'scroll',
      checkPagination
    )

    window.removeEventListener(
      'resize',
      checkPagination
    )
  }
}, [
  shouldPaginate,
  hasNextPage,
  isLoading,
  isFetchingNextPage,
  fetchNextPage,
  rows.length,
  rowVirtualizer,
  totalSize,
  scrollEl,
])
  const containerClass = `${style.cards_container} ${drawerOpen ? style.cards_container_drawer_open : ''}`

  /**
   *  Prefetch root FIX:
   * - If the page scrolls (scrollEl=documentElement/body), IO root MUST be null (viewport).
   * - If a container scrolls, IO root should be that container.
   *
   * This prevents “everything intersects → prefetch everything”.
   */
  const vaultRoot: Element | null =
    scrollEl && !isWindowScrollEl(scrollEl) ? scrollEl : null

  // Keep tight — you asked for ~232px.
  const vaultRootMargin = '232px 0px 232px 0px'

  return (
    <div ref={gridRef} className={containerClass}>
      {showInitialLoader && (
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            padding: '2rem 0',
          }}
        >
          <BeatLoader size={8} />
        </div>
      )}

      {showEmptyState && (
        <div
          style={{
            width: '100%',
            padding: '2rem 0',
            textAlign: 'center',
            opacity: 0.7,
          }}
        >
          <p style={{ fontSize: '1.4rem' }}>No Shared Media</p>
        </div>
      )}

      {rows.length > 0 && (
        <div style={{ position: 'relative', width: '100%', height: totalSize }}>
          {virtualRows.map((vr) => {
            const row = rows[vr.index]
            if (!row) return null

            return (
              <div
                key={vr.key}
                ref={rowVirtualizer.measureElement}
                data-index={vr.index}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${vr.start}px)`,
                }}
              >
                <div className={style.vault_virtual_row}>
                  {row.map((m: any, colIndex: number) => (
                    <div key={`${vr.index}-${colIndex}-${String(m?.__k ?? m?.id ?? 'x')}`} className={style.vault_grid_item}>                     
                     <VaultCardGrouped
                        onCirlceClick={toggleSelectedPosts}
                        media={m}
                        selected={allSelectedMedia.some((x: any) => x.id === m.id)}
                        selectedText={selectedPosts.indexOf(m.id) + 1}
                        grouped={postsGrouped}
                        showDuplicates={showDuplicates}
                        clientSortKey={postsSort}
                        allTimeStats={allTimeStats}
                        restrictType={postsFilter}
                        onSelectVariant={onSelectVariant}
                        isVariantSelected={isVariantSelected}
                        getVariantIndex={getVariantIndex}
                        vaultRoot={vaultRoot}
                        vaultRootMargin={vaultRootMargin}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}

    {shouldPaginate && (hasNextPage ?? isLoading) && (
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          padding: '1rem 0',
        }}
      >
        {isFetchingNextPage && <BeatLoader size={8} />}
      </div>
    )}
    </div>
  )
}
